import React from 'react';
import { motion } from 'framer-motion';
import { FaHistory, FaUniversity, FaAward, FaChartLine, FaUsers, FaGlobeAsia } from 'react-icons/fa';
import { GiStonePath, GiModernCity } from 'react-icons/gi';
import { IoMdSchool } from 'react-icons/io';
import './history.css';

const History = () => {
  const milestones = [
    {
      year: "1985",
      title: "Foundation Established",
      description: "Asian Technical Institute founded with focus on vocational engineering education",
      icon: <GiStonePath size={30} />,
      highlight: false
    },
    {
      year: "1992",
      title: "First Degree Programs",
      description: "Launched bachelor's programs in Mechanical and Electrical Engineering",
      icon: <IoMdSchool size={30} />,
      highlight: true
    },
    {
      year: "1998",
      title: "University Status Achieved",
      description: "Granted full university charter as Asian Polytechnic Institute",
      icon: <FaUniversity size={30} />,
      highlight: false
    },
    {
      year: "2005",
      title: "Research Expansion",
      description: "Established 5 research centers with government and industry funding",
      icon: <FaChartLine size={30} />,
      highlight: false
    },
    {
      year: "2012",
      title: "International Recognition",
      description: "Ranked among top 50 technical universities in Asia",
      icon: <FaGlobeAsia size={30} />,
      highlight: true
    },
    {
      year: "2018",
      title: "Smart Campus Initiative",
      description: "Completed $150M campus modernization with IoT infrastructure",
      icon: <GiModernCity size={30} />,
      highlight: false
    },
    {
      year: "2023",
      title: "40,000+ Alumni",
      description: "Celebrated graduation of our 40,000th student with global alumni network",
      icon: <FaUsers size={30} />,
      highlight: false
    }
  ];

  const historicalFigures = [
    {
      name: "Dr. Liang Wei",
      role: "Founding President (1985-1995)",
      contribution: "Established the institution's technical education philosophy",
      image: "/images/history/founder.jpg"
    },
    {
      name: "Prof. Aminah binti Yusof",
      role: "First Female Dean (1998-2005)",
      contribution: "Pioneered women in STEM initiatives across Southeast Asia",
      image: "/images/history/dean.jpg"
    },
    {
      name: "Dr. Rajiv Kapoor",
      role: "Research Director (2006-2018)",
      contribution: "Transformed research output with industry partnerships",
      image: "/images/history/research.jpg"
    }
  ];

  const stats = [
    { value: "38", label: "Years of Excellence" },
    { value: "12", label: "Schools & Departments" },
    { value: "120+", label: "Nationalities Represented" },
    { value: "5", label: "Presidential Awards" }
  ];

  const timelineVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <motion.div 
      className="history-container"
      initial="hidden"
      animate="visible"
      variants={timelineVariants}
    >
      {/* Hero Section */}
      <div className="history-hero">
        <motion.div 
          className="hero-content"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1><FaHistory /> Our Legacy of Innovation</h1>
          <p className="hero-subtitle">From technical institute to Asia's premier polytechnic university</p>
          <div className="hero-stats">
            {stats.map((stat, index) => (
              <motion.div 
                key={index}
                className="stat-card"
                whileHover={{ scale: 1.05 }}
                variants={itemVariants}
              >
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Timeline Section */}
      <section className="timeline-section">
        <div className="section-header">
          <h2><FaUniversity /> Institutional Milestones</h2>
          <p>Key moments in our journey of academic excellence</p>
        </div>
        
        <div className="timeline">
          {milestones.map((milestone, index) => (
            <motion.div 
              key={index}
              className={`timeline-item ${milestone.highlight ? 'highlight' : ''}`}
              variants={itemVariants}
              whileHover={{ scale: 1.03 }}
            >
              <div className="timeline-year">{milestone.year}</div>
              <div className="timeline-content">
                <div className="timeline-icon">{milestone.icon}</div>
                <h3>{milestone.title}</h3>
                <p>{milestone.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Historical Figures */}
      <section className="figures-section">
        <div className="section-header">
          <h2><FaUsers /> Visionary Leaders</h2>
          <p>The pioneers who shaped our institution</p>
        </div>
        
        <motion.div 
          className="figures-grid"
          variants={timelineVariants}
        >
          {historicalFigures.map((figure, index) => (
            <motion.div 
              key={index}
              className="figure-card"
              variants={itemVariants}
              whileHover={{ y: -5 }}
            >
              <div className="figure-image">
                <img src={figure.image} alt={figure.name} />
                <div className="image-overlay"></div>
              </div>
              <div className="figure-info">
                <h3>{figure.name}</h3>
                <p className="figure-role">{figure.role}</p>
                <p className="figure-contribution">{figure.contribution}</p>
                <button className="learn-more">Learn More</button>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Campus Evolution */}
      <section className="evolution-section">
        <motion.div 
          className="evolution-content"
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="evolution-text">
            <h2><GiModernCity /> Campus Evolution</h2>
            <p>From our humble beginnings in a single building to today's 50-acre smart campus with cutting-edge facilities that blend tradition with innovation.</p>
            <div className="campus-stats">
              <div className="campus-stat">
                <div className="stat-number">5</div>
                <div className="stat-desc">Architectural Awards</div>
              </div>
              <div className="campus-stat">
                <div className="stat-number">250%</div>
                <div className="stat-desc">Growth Since 2000</div>
              </div>
            </div>
          </div>
          <div className="evolution-images">
            {/* These would be actual images in production */}
            <div className="old-campus">1985 Campus</div>
            <div className="new-campus">2023 Campus</div>
          </div>
        </motion.div>
      </section>

      {/* Legacy & Future */}
      <section className="legacy-section">
        <div className="legacy-content">
          <FaAward size={60} />
          <h2>Honoring Our Past, Building Our Future</h2>
          <p>As we reflect on 38 years of achievements, we continue our commitment to technical education excellence with our 2030 Strategic Vision.</p>
          <button className="cta-button">Explore Our Strategic Plan</button>
        </div>
      </section>
    </motion.div>
  );
};

export default History;